// src/services/authSocketService.ts
// WebSocket service for SSO force logout (IdP-initiated SLO)

import { Socket, Channel } from 'phoenix';
import { getAuthToken, decodeToken } from './authService';

const WRITE_API_URL = process.env.REACT_APP_FLUX_API_URL || '';

// Extract WebSocket URL from API URL
function getSocketUrl(): string {
  // Convert http(s)://host/path to ws(s)://host/auth_socket
  // Phoenix Socket class automatically appends /websocket
  const url = new URL(WRITE_API_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}/auth_socket`;
}

export interface ForceLogoutPayload {
  reason: string;
  timestamp: string;
}

export type ForceLogoutCallback = (payload: ForceLogoutPayload) => void;

class AuthSocketService {
  private socket: Socket | null = null;
  private channel: Channel | null = null;
  private forceLogoutCallback: ForceLogoutCallback | null = null;

  /**
   * Connect to the auth WebSocket and join the user's channel
   */
  connect(onForceLogout: ForceLogoutCallback): void {
    const token = getAuthToken();
    if (!token) {
      console.warn('[AuthSocket] No auth token available, skipping connection');
      return;
    }

    // Get user ID from JWT - use user_id claim (the public_id)
    const decoded = decodeToken(token);
    const userId = decoded?.user_id;
    if (!userId) {
      console.warn('[AuthSocket] Could not extract user_id from token');
      return;
    }

    this.forceLogoutCallback = onForceLogout;

    // Create socket connection with token in params
    this.socket = new Socket(getSocketUrl(), {
      params: { token },
      reconnectAfterMs: (tries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 10s max
        return Math.min(1000 * Math.pow(2, tries - 1), 10000);
      },
    });

    // Socket event handlers
    this.socket.onOpen(() => {
      console.log('[AuthSocket] Connected');
    });

    this.socket.onError((error) => {
      console.error('[AuthSocket] Socket error:', error);
    });

    this.socket.onClose(() => {
      console.log('[AuthSocket] Socket closed');
    });

    // Connect the socket
    this.socket.connect();

    // Join the user's auth channel
    this.joinChannel(userId as string);
  }

  /**
   * Join the auth channel for the given user ID
   */
  private joinChannel(userId: string): void {
    if (!this.socket) return;

    const topic = `auth:${userId}`;
    this.channel = this.socket.channel(topic, {});

    // Listen for force_logout events
    this.channel.on('force_logout', (payload: ForceLogoutPayload) => {
      console.log('[AuthSocket] Received force_logout:', payload);
      if (this.forceLogoutCallback) {
        this.forceLogoutCallback(payload);
      }
    });

    // Join the channel
    this.channel
      .join()
      .receive('ok', () => {
        console.log(`[AuthSocket] Joined channel ${topic}`);
      })
      .receive('error', (resp) => {
        console.error(`[AuthSocket] Failed to join channel ${topic}:`, resp);
        this.handleJoinError(resp);
      })
      .receive('timeout', () => {
        console.error(`[AuthSocket] Timeout joining channel ${topic}`);
      });
  }

  /**
   * Handle channel join errors
   */
  private handleJoinError(resp: unknown): void {
    // If unauthorized, the token may be invalid - trigger logout
    if (typeof resp === 'object' && resp !== null) {
      const errorResp = resp as { reason?: string };
      if (errorResp.reason === 'unauthorized' || errorResp.reason === 'invalid_token') {
        console.warn('[AuthSocket] Unauthorized - triggering logout');
        if (this.forceLogoutCallback) {
          this.forceLogoutCallback({
            reason: 'session_expired',
            timestamp: new Date().toISOString(),
          });
        }
      }
    }
  }

  /**
   * Disconnect from the WebSocket
   */
  disconnect(): void {
    if (this.channel) {
      this.channel.leave();
      this.channel = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.forceLogoutCallback = null;
    console.log('[AuthSocket] Disconnected');
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.channel !== null;
  }

  /**
   * Reconnect with a new token (e.g., after token refresh)
   */
  reconnect(onForceLogout: ForceLogoutCallback): void {
    this.disconnect();
    this.connect(onForceLogout);
  }
}

// Export singleton instance
export const authSocketService = new AuthSocketService();
